import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, X } from 'lucide-react';
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_NAMES,
  LANGUAGE_CODES,
  markLanguageExplicit,
  type SupportedLanguage,
} from '@/i18n';

interface LanguageSwitcherProps {
  /** Custom class name */
  className?: string;
  /** Compact mode - smaller trigger button for footer */
  compact?: boolean;
}

/**
 * Mobile-friendly language switcher for Visitor PWA.
 * Opens a bottom sheet with all available languages.
 */
export function LanguageSwitcher({ className = '', compact = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const currentLanguage = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage;

  const handleLanguageChange = (lang: SupportedLanguage) => {
    markLanguageExplicit();
    i18n.changeLanguage(lang);
    setIsOpen(false);
  };

  // Close on Escape, matches IssueSheet / BuildingSheet pattern so keyboard
  // users aren't trapped when the sheet is open.
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen]);

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-1.5 rounded-full
          transition-colors touch-manipulation
          ${
            compact
              ? 'px-3 py-3 min-h-[48px] text-[15px] text-muted-foreground hover:text-foreground hover:bg-muted'
              : 'px-3 py-2 min-h-[48px] text-[15px] bg-muted hover:bg-warm-border text-foreground'
          } ${className}`}
        aria-label={t('settings.language')}
      >
        <Globe className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        <span className="font-medium">{LANGUAGE_CODES[currentLanguage]}</span>
      </button>

      {/* Bottom Sheet Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="language-sheet-title"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setIsOpen(false)}
          />

          {/* Sheet */}
          <div className="relative w-full max-w-lg bg-white rounded-t-2xl animate-slide-up safe-area-bottom">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-warm-border rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-warm-border">
              <h2 id="language-sheet-title" className="text-lg font-semibold text-foreground">
                {t('settings.language')}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="flex h-12 w-12 items-center justify-center -me-2 rounded-full hover:bg-warm-faint active:bg-muted transition-colors touch-manipulation"
                aria-label={t('buttons.close')}
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Language Options */}
            <div className="py-2 max-h-[60vh] overflow-y-auto overscroll-contain">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isSelected = currentLanguage === lang;
                return (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`w-full flex items-center gap-4 px-4 py-4
                      hover:bg-warm-faint active:bg-muted
                      transition-colors touch-manipulation
                      ${isSelected ? 'bg-primary/5' : ''}`}
                    aria-current={isSelected ? 'true' : undefined}
                  >
                    {/* Language Code Badge */}
                    <span
                      className={`flex items-center justify-center w-10 h-10 rounded-full text-[15px] font-bold
                      ${isSelected ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}
                    >
                      {LANGUAGE_CODES[lang]}
                    </span>

                    {/* Language Name */}
                    <span
                      className={`flex-1 text-start text-base
                      ${isSelected ? 'text-primary font-semibold' : 'text-foreground'}`}
                    >
                      {LANGUAGE_NAMES[lang]}
                    </span>

                    {/* Checkmark */}
                    {isSelected && <Check aria-hidden="true" className="w-5 h-5 text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Safe area padding for iOS */}
            <div className="h-6" />
          </div>
        </div>
      )}
    </>
  );
}
